import { Injectable } from '@angular/core';
import { HostelSearchParams } from '@app/search.service';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { ApiService, Room, Rooms } from '@app/services/api/api.service';
import Skyflow from 'skyflow-js';
import { EnvironmentService } from '@app/environment.service';

export interface BedSelection {
  room: Room;
  rates: Rates;
}

export interface Rates {
  standard: number;
  nonRefundable: number;
  breakfastIncluded: number;
}

export type RateType = keyof Rates;

export interface SkyflowResp {
  records: {
    fields: {
      cvv: string;
      skyflow_id: string;
    };
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class BookingService {
  public bookingOptions = new BehaviorSubject<any>(0);
  public paymentInitialized = false;

  private selectedBeds = new Map<string, BedSelection>();
  private selectedBedsSubject = new BehaviorSubject<Map<string, BedSelection>>(
    this.selectedBeds
  );

  public selectedBeds$ = this.selectedBedsSubject.asObservable();
  public skyflow: any;

  public booking: any;
  public rooms$: BehaviorSubject<Rooms> = new BehaviorSubject<Rooms>(null);
  private token: any;

  constructor(
    private environmentService: EnvironmentService,
    private apiService: ApiService
  ) {}

  updateRooms(rooms: Rooms) {
    this.rooms$.next(rooms);
  }

  addBed(room: Room, rate: RateType): void {
    const id = room.id || room._id;
    const currentSelection =
      this.selectedBeds.get(id) ||
      ({
        room,
        rates: { standard: 0, nonRefundable: 0, breakfastIncluded: 0 },
      } as BedSelection);
    currentSelection.rates[rate] += 1;
    this.selectedBeds.set(id, currentSelection);
    this.updateSelectedBeds();
  }

  removeBed(room: Room, rate: RateType): void {
    const id = room.id || room._id;
    const currentSelection = this.selectedBeds.get(id);
    if (currentSelection && currentSelection.rates[rate] > 0) {
      currentSelection.rates[rate] -= 1;
      if (this.isRoomSelectionEmpty(currentSelection.rates)) {
        this.selectedBeds.delete(id);
      } else {
        this.selectedBeds.set(id, currentSelection);
      }
      this.updateSelectedBeds();
    }
  }

  private isRoomSelectionEmpty(rates: Rates): boolean {
    return (
      rates.standard === 0 &&
      rates.nonRefundable === 0 &&
      rates.breakfastIncluded === 0
    );
  }

  private updateSelectedBeds(): void {
    this.selectedBedsSubject.next(this.selectedBeds);
  }

  getSelectedBeds(room: Room): BedSelection {
    const id = room.id || room._id;
    return this.selectedBeds.get(id);
  }

  clearSelection(): void {
    this.selectedBeds.clear();
    this.updateSelectedBeds();
  }
}

export interface SkyflowCardOptions {
  cardNumber: string;
  cvv: string;
  expirationDate: string;
  cardHolderName: string;
}
